let currentLang = 'ar';
const DEFAULT_LANG = 'ar';
const SUPPORTED_LANGS = ['ar', 'it', 'fr', 'es', 'en'];
const translationsCache = {};
let activeTranslationRequest = 0;

function normalizeLang(lang) {
  const candidate = typeof lang === 'string' ? lang.toLowerCase() : DEFAULT_LANG;
  return SUPPORTED_LANGS.includes(candidate) ? candidate : DEFAULT_LANG;
}

function getI18nConfig() {
  if (typeof muhLandingI18nData !== 'undefined' && muhLandingI18nData) {
    return muhLandingI18nData;
  }
  return { i18nBase: '' };
}

function getTranslationValue(key, lang) {
  const normalizedLang = normalizeLang(lang);
  const dictionary = translationsCache[normalizedLang] || translationsCache[DEFAULT_LANG] || {};
  return dictionary[key] || '';
}

async function fetchTranslations(lang) {
  const normalizedLang = normalizeLang(lang);
  if (translationsCache[normalizedLang]) return translationsCache[normalizedLang];
  const preloaded = getI18nConfig().translations?.[normalizedLang];
  if (preloaded && typeof preloaded === 'object') return translationsCache[normalizedLang] = preloaded;
  const response = await fetch(getI18nConfig().i18nBase + 'i18n-' + normalizedLang + '.json', { credentials: 'omit' });
  if (!response.ok) throw new Error('Failed to load translations (' + response.status + ')');
  const data = await response.json();
  if (!data || typeof data !== 'object') throw new Error('Invalid translations payload');
  return translationsCache[normalizedLang] = data;
}

function applyTranslationsTo(root, lang) {
  if (!root) {
    return;
  }

  const normalizedLang = normalizeLang(lang);
  root.querySelectorAll('[data-key]').forEach(element => {
    const key = element.getAttribute('data-key');
    const text = getTranslationValue(key, normalizedLang);
    if (text) {
      element.innerHTML = resolveDynamicTokens(element, text);
    }
  });
}

function resolveDynamicTokens(element, text) {
  if (!element || typeof text !== 'string') {
    return text;
  }

  const salePrice = element.getAttribute('data-sale-price');
  const regularPrice = element.getAttribute('data-regular-price');
  let output = text;

  if (salePrice !== null) {
    output = output.replaceAll('{sale}', salePrice);
  }

  if (regularPrice !== null) {
    output = output.replaceAll('{regular}', regularPrice);
  }

  return output;
}

function setLanguageMeta(lang) {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  if (document.body) {
    document.body.lang = lang;
  }
}

function setLangSwitcherLoading(isLoading) {
  const dropdown = document.getElementById('langDropdown');
  if (!dropdown) {
    return;
  }

  dropdown.classList.toggle('is-loading', !!isLoading);
  dropdown.querySelectorAll('.lang-option').forEach(option => {
    option.style.pointerEvents = isLoading ? 'none' : '';
    option.style.opacity = isLoading ? '0.65' : '';
  });
}

async function changeLanguage(lang) {
  const requestedLang = normalizeLang(lang);
  const requestId = ++activeTranslationRequest;
  setLangSwitcherLoading(true);

  let finalLang = requestedLang;

  try {
    try {
      await fetchTranslations(requestedLang);
    } catch (error) {
      if (requestedLang !== DEFAULT_LANG) {
        try {
          await fetchTranslations(DEFAULT_LANG);
        } catch (fallbackError) {
        }
      }
      finalLang = DEFAULT_LANG;
    }

    if (requestId !== activeTranslationRequest) {
      return;
    }

    currentLang = finalLang;
    setLanguageMeta(finalLang);
    applyTranslationsTo(document, finalLang);

    if (typeof updatePopupTranslations === 'function') {
      updatePopupTranslations();
    }

    changeFormLang(finalLang);

    const dropdown = document.getElementById('langDropdown');
    if (dropdown) {
      dropdown.classList.remove('show');
    }

    localStorage.setItem('selectedLanguage', finalLang);
    document.querySelectorAll('#selected_lang').forEach(el => {
      el.value = finalLang;
    });

    updateLangToggle(finalLang);
    const feedbackSection = document.querySelector('.feedback');
    if (feedbackSection && feedbackSection.dataset.feedbackGridInit === '1') {
      const toggle = feedbackSection.querySelector('[data-feedback-toggle]');
      if (toggle) {
        updateFeedbackToggleLabel(toggle, feedbackSection.classList.contains('is-expanded'));
      }
    }
    document.dispatchEvent(new CustomEvent('muh:languageChanged', { detail: { lang: finalLang } }));
  } finally {
    if (requestId === activeTranslationRequest) {
      setLangSwitcherLoading(false);
    }
  }
}

function changeFormLang(lang) {
  document.querySelectorAll('.muh-eform').forEach(el => {
    el.style.display = el.dataset.lang === lang ? 'block' : 'none';
  });
}

function toggleLangDropdown() {
  const dropdown = document.getElementById('langDropdown');
  if (dropdown) {
    dropdown.classList.toggle('show');
  }
}

function updateLangToggle(lang) {
  const flag = document.getElementById('langCurrentFlag');
  const label = document.getElementById('langCurrentLabel');
  if (!flag || !label) return;

  const map = {
    ar: {
      label: 'AR',
      flag: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAALCAMAAABBPP0LAAAAQlBMVEX/AAD9AAD2AADxCAjqAAD/eXn+cHH8ZGT9Wlr5U1T2SUf9T0/8R0b5Pz/3NjX2Li71Jia7dzj0GBjRYz3DXSzhAAAcsELyAAAAVUlEQVR4AQXBMW7DQBAAMc6uDkiRxv9/pVrbiEIGQSg5iCi9L5aVUm8DK6WsidXfudQM217y8zjP8PwOqs/ud7hzqdSn4caVVIYbmlPmWUlOo8AL+AdhWQ1voS2nuwAAAABJRU5ErkJggg=='
    },
    it: {
      label: 'IT',
      flag: 'https://upload.wikimedia.org/wikipedia/commons/0/03/Flag_of_Italy.svg'
    },
    fr: {
      label: 'FR',
      flag: 'https://upload.wikimedia.org/wikipedia/commons/c/c3/Flag_of_France.svg'
    },
    es: {
      label: 'ES',
      flag: 'https://upload.wikimedia.org/wikipedia/commons/9/9a/Flag_of_Spain.svg'
    },
    en: {
      label: 'UK',
      flag: 'https://upload.wikimedia.org/wikipedia/commons/8/83/Flag_of_the_United_Kingdom_%283-5%29.svg'
    }
  };

  const selected = map[lang] || map.ar;
  flag.src = selected.flag;
  label.textContent = selected.label;
}

function updateFeedbackToggleLabel(button, isExpanded) {
  if (!button) {
    return;
  }

  const moreKey = button.getAttribute('data-more-key') || 'feedback.grid.load_more';
  const lessKey = button.getAttribute('data-less-key') || 'feedback.grid.load_less';
  const key = isExpanded ? lessKey : moreKey;
  const translated = getTranslationValue(key, currentLang);
  button.innerHTML = translated || button.textContent || '';
}

function extractFeedbackVideoId(url, provider) {
  const value = String(url || '').trim();
  if (!value) {
    return '';
  }

  if (provider === 'youtube') {
    const patterns = [
      /[?&]v=([^&]+)/i,
      /youtu\.be\/([^?&#/]+)/i,
      /youtube\.com\/shorts\/([^?&#/]+)/i,
      /youtube\.com\/embed\/([^?&#/]+)/i
    ];

    for (let i = 0; i < patterns.length; i += 1) {
      const match = value.match(patterns[i]);
      if (match && match[1]) {
        return match[1];
      }
    }
  }

  if (provider === 'vimeo') {
    const match = value.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    return match && match[1] ? match[1] : '';
  }

  return '';
}

function buildFeedbackEmbed(url, muted) {
  const value = String(url || '').trim();
  if (!value) {
    return null;
  }

  const isMuted = muted === true;

  if (/youtu(?:\.be|be\.com)/i.test(value)) {
    const videoId = extractFeedbackVideoId(value, 'youtube');
    if (!videoId) {
      return null;
    }

    return {
      type: 'iframe',
      src: 'https://www.youtube.com/embed/' + videoId + '?autoplay=1&controls=1&rel=0&modestbranding=1&playsinline=1&mute=' + (isMuted ? '1' : '0'),
      allow: 'autoplay; encrypted-media; fullscreen; picture-in-picture; web-share'
    };
  }

  if (/vimeo\.com/i.test(value)) {
    const videoId = extractFeedbackVideoId(value, 'vimeo');
    if (!videoId) {
      return null;
    }

    return {
      type: 'iframe',
      src: 'https://player.vimeo.com/video/' + videoId + '?autoplay=1&controls=1&title=0&byline=0&portrait=0&dnt=1&muted=' + (isMuted ? '1' : '0'),
      allow: 'autoplay; fullscreen; picture-in-picture'
    };
  }

  if (/loom\.com/i.test(value)) {
    try {
      const url = new URL(value, window.location.href);
      if (url.pathname.indexOf('/share/') === 0) {
        url.pathname = url.pathname.replace('/share/', '/embed/');
      }
      url.searchParams.set('autoplay', '1');
      url.searchParams.set('hideEmbedTopBar', 'true');
      url.searchParams.set('hide_share', 'true');
      url.searchParams.set('hide_title', 'true');
      url.searchParams.set('hide_owner', 'true');

      return {
        type: 'iframe',
        src: url.toString(),
        allow: 'autoplay; fullscreen; picture-in-picture; web-share'
      };
    } catch (error) {
      return null;
    }
  }

  return {
    type: 'video',
    src: value
  };
}

function deriveFeedbackThumbFromUrl(url) {
  const value = String(url || '').trim();
  if (!value) {
    return '';
  }

  if (/youtu(?:\.be|be\.com)/i.test(value)) {
    const videoId = extractFeedbackVideoId(value, 'youtube');
    return videoId ? 'https://img.youtube.com/vi/' + videoId + '/hqdefault.jpg' : '';
  }

  if (/vimeo\.com/i.test(value)) {
    const videoId = extractFeedbackVideoId(value, 'vimeo');
    return videoId ? 'https://vumbnail.com/' + videoId + '.jpg' : '';
  }

  return '';
}

function setFeedbackCardThumb(card, thumbUrl) {
  if (!card) {
    return;
  }

  const value = String(thumbUrl || '').trim();
  if (!value) {
    return;
  }

  const thumbContainer = card.querySelector('.feedback-video-thumb');
  if (!thumbContainer) {
    return;
  }

  let thumbImage = thumbContainer.querySelector('img');
  if (!thumbImage) {
    thumbImage = document.createElement('img');
    thumbImage.alt = '';
    thumbImage.loading = 'lazy';
    thumbImage.decoding = 'async';
    const placeholder = thumbContainer.querySelector('.feedback-video-thumb-placeholder');
    if (placeholder) {
      placeholder.replaceWith(thumbImage);
    } else {
      thumbContainer.insertBefore(thumbImage, thumbContainer.firstChild);
    }
  }

  thumbImage.src = value;
  card.setAttribute('data-feedback-video-thumb', value);
}

function initFeedbackGrid() {
  const section = document.querySelector('.feedback:not([data-feedback-grid-init="1"])');
  if (!section) {
    return;
  }

  const grid = section.querySelector('[data-feedback-grid]');
  const toggle = section.querySelector('[data-feedback-toggle]');
  const cards = Array.from(section.querySelectorAll('[data-feedback-video-item]'));
  const triggers = Array.from(section.querySelectorAll('[data-feedback-video-trigger]'));

  if (!grid || cards.length === 0) {
    return;
  }

  section.dataset.feedbackGridInit = '1';
  let activeCard = null;

  const syncState = () => {
    const expanded = section.classList.contains('is-expanded');
    grid.classList.toggle('is-expanded', expanded);
    if (toggle) {
      updateFeedbackToggleLabel(toggle, expanded);
    }
  };

  const restoreCard = (card) => {
    if (!card) {
      return;
    }

    const thumb = card.querySelector('.feedback-video-thumb');
    const originalHtml = card.getAttribute('data-feedback-original-thumb-html') || '';
    if (thumb && originalHtml) {
      thumb.innerHTML = originalHtml;
    }
    card.classList.remove('is-active');
    card.setAttribute('aria-pressed', 'false');
    card.setAttribute('data-feedback-playing', '0');
  };

  const createCloseButton = (card) => {
    const thumb = card.querySelector('.feedback-video-thumb');
    if (!thumb) {
      return null;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'feedback-inline-close';
    button.setAttribute('aria-label', 'Close video');
    button.innerHTML = '<i class="fa-solid fa-xmark"></i>';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (activeCard === card) {
        restoreCard(card);
        activeCard = null;
      }
    });
    return button;
  };

  const openCard = (card) => {
    if (!card) {
      return;
    }

    const url = String(card.getAttribute('data-feedback-video-url') || '').trim();
    const title = String(card.getAttribute('data-feedback-video-title') || '').trim();
    const embed = buildFeedbackEmbed(url, false);

    if (!embed) {
      return;
    }

    document.dispatchEvent(new CustomEvent('muh:videoStarted', { detail: { source: card } }));

    if (activeCard && activeCard !== card) {
      restoreCard(activeCard);
    }

    const thumb = card.querySelector('.feedback-video-thumb');
    if (!thumb) {
      return;
    }

    if (!card.getAttribute('data-feedback-original-thumb-html')) {
      card.setAttribute('data-feedback-original-thumb-html', thumb.innerHTML);
    }

    const derivedThumb = String(card.getAttribute('data-feedback-video-thumb') || '').trim() || deriveFeedbackThumbFromUrl(url);
    const media = embed.type === 'iframe'
      ? (() => {
          const iframe = document.createElement('iframe');
          iframe.src = embed.src;
          iframe.title = title || 'Feedback video';
          iframe.loading = 'lazy';
          iframe.allow = embed.allow;
          iframe.setAttribute('allowfullscreen', 'allowfullscreen');
          iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
          return iframe;
        })()
      : (() => {
          const video = document.createElement('video');
          video.controls = true;
          video.autoplay = true;
          video.playsInline = true;
          video.muted = false;
          video.preload = 'metadata';
          video.src = embed.src;
          if (derivedThumb) {
            video.poster = derivedThumb;
            setFeedbackCardThumb(card, derivedThumb);
          }
          return video;
        })();

    thumb.innerHTML = '';
    thumb.appendChild(media);
    const closeButton = createCloseButton(card);
    if (closeButton) {
      thumb.appendChild(closeButton);
    }

    activeCard = card;
    card.classList.add('is-active');
    card.setAttribute('aria-pressed', 'true');
  };

  if (toggle) {
    toggle.addEventListener('click', () => {
      section.classList.toggle('is-expanded');
      syncState();
    });
  }

  document.addEventListener('muh:videoStarted', (event) => {
    const source = event.detail && event.detail.source;
    if (activeCard && activeCard !== source) {
      restoreCard(activeCard);
      activeCard = null;
    }
  });

  triggers.forEach((trigger) => {
    const card = trigger.closest('[data-feedback-video-item]');
    if (card) {
      card.setAttribute('aria-pressed', 'false');
      const thumb = card.querySelector('.feedback-video-thumb');
      if (thumb && !card.getAttribute('data-feedback-original-thumb-html')) {
        card.setAttribute('data-feedback-original-thumb-html', thumb.innerHTML);
      }
    }

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const activeTriggerCard = trigger.closest('[data-feedback-video-item]');
      if (!activeTriggerCard) {
        return;
      }

      if (activeCard === activeTriggerCard && activeTriggerCard.classList.contains('is-active')) {
        restoreCard(activeTriggerCard);
        activeCard = null;
        return;
      }

      openCard(activeTriggerCard);
    });
  });

  syncState();
}
// إغلاق قائمة اللغات عند النقر خارجها
document.addEventListener('click', function(e) {
  const dropdown = document.getElementById('langDropdown');
  const button = document.querySelector('.lang-toggle');
  if (!dropdown || !button) {
    return;
  }
  if (!button.contains(e.target) && !dropdown.contains(e.target)) {
    dropdown.classList.remove('show');
  }
});

// تأثير التمرير السلس للروابط
document.querySelectorAll('a[href^="#"]').forEach(anchor => {
  anchor.addEventListener('click', function(e) {
    e.preventDefault();
    const targetId = this.getAttribute('href');
    if (targetId === '#') return;

    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      let offset = 0;
      const pageHeader = document.querySelector('.landing-header');

      if (pageHeader) {
        const headerStyle = window.getComputedStyle(pageHeader);
        const isOverlayHeader = headerStyle.position === 'fixed' || headerStyle.position === 'sticky';

        if (isOverlayHeader) {
          offset = pageHeader.getBoundingClientRect().height;
        }
      }

      window.scrollTo({
        top: Math.max(targetElement.offsetTop - offset, 0),
        behavior: 'smooth'
      });
    }
  });
});



// تحميل اللغة المحفوظة عند بدء التحميل
let muhCountdownTickerId = null;

function initNativeSlider() {
  document.querySelectorAll('[data-muh-slider]').forEach((slider) => {
    if (slider.dataset.sliderInit === '1') {
      return;
    }

    const track = slider.querySelector('.muh-slider-track');
    const slides = Array.from(slider.querySelectorAll('.muh-slider-slide'));
    const prevBtn = slider.querySelector('[data-muh-slider-prev]');
    const nextBtn = slider.querySelector('[data-muh-slider-next]');
    const dotsWrap = slider.querySelector('[data-muh-slider-dots]');

    if (!track || slides.length === 0) {
      return;
    }

    slider.dataset.sliderInit = '1';

    let index = 0;
    let autoTimer = null;
    let perView = 5;
    let maxIndex = 0;
    const desktopPerView = Math.max(1, parseInt(slider.dataset.sliderPerViewDesktop || '5', 10) || 5);
    const tabletPerView = Math.max(1, parseInt(slider.dataset.sliderPerViewTablet || '3', 10) || 3);
    const mobilePerView = Math.max(1, parseInt(slider.dataset.sliderPerViewMobile || '3', 10) || 3);
    const autoEnabled = slider.dataset.sliderAuto !== 'false';

    const setPerView = () => {
      const width = window.innerWidth;
      if (width <= 767) {
        perView = mobilePerView;
      } else if (width <= 1024) {
        perView = tabletPerView;
      } else {
        perView = desktopPerView;
      }
      slider.style.setProperty('--muh-slides-per-view', String(perView));
      maxIndex = Math.max(0, slides.length - perView);
      if (index > maxIndex) {
        index = maxIndex;
      }
    };

    const renderDots = () => {
      if (!dotsWrap) {
        return;
      }
      dotsWrap.innerHTML = '';
      const pages = maxIndex + 1;
      if (pages <= 1) {
        return;
      }
      for (let i = 0; i < pages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'muh-slider-dot' + (i === index ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Slide ' + (i + 1));
        dot.addEventListener('click', () => {
          index = i;
          render();
          restartAuto();
        });
        dotsWrap.appendChild(dot);
      }
    };

    const render = () => {
      const offset = (index * 100) / perView;
      track.style.transform = 'translateX(-' + offset + '%)';
      const dots = dotsWrap ? dotsWrap.querySelectorAll('.muh-slider-dot') : [];
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
      });
    };

    const next = () => {
      index = index >= maxIndex ? 0 : index + 1;
      render();
    };

    const prev = () => {
      index = index <= 0 ? maxIndex : index - 1;
      render();
    };

    const startAuto = () => {
      if (!autoEnabled) {
        return;
      }
      if (autoTimer) {
        clearInterval(autoTimer);
      }
      if (maxIndex <= 0) {
        return;
      }
      autoTimer = setInterval(next, 5000);
    };

    const restartAuto = () => {
      startAuto();
    };

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        next();
        restartAuto();
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        prev();
        restartAuto();
      });
    }

    slider.addEventListener('mouseenter', () => {
      if (autoTimer) {
        clearInterval(autoTimer);
      }
    });

    slider.addEventListener('mouseleave', startAuto);

    const onResize = () => {
      setPerView();
      renderDots();
      render();
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    setPerView();
    renderDots();
    render();
    startAuto();
  });
}

function formatCountdownParts(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return {
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0')
  };
}

function renderLoopCountdown() {
  const countdowns = document.querySelectorAll('[data-muh-countdown]');
  if (!countdowns.length) {
    return;
  }

  countdowns.forEach((countdown) => {
    const days = parseInt(countdown.getAttribute('data-cycle-days') || '3', 10);
    const cycleMs = Math.max(1, days) * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const elapsed = now % cycleMs;
    let remaining = cycleMs - elapsed;
    if (remaining === cycleMs) {
      remaining = 0;
    }
    const parts = formatCountdownParts(remaining);

    Object.keys(parts).forEach((unit) => {
      const target = countdown.querySelector('[data-unit="' + unit + '"]');
      if (target) {
        target.textContent = parts[unit];
      }
    });
  });
}

function initLoopCountdown() {
  renderLoopCountdown();
  if (muhCountdownTickerId) {
    return;
  }
  muhCountdownTickerId = window.setInterval(renderLoopCountdown, 1000);
}

function initLandingDynamicBlocks() {
  initNativeSlider();
  initLoopCountdown();
  initFeedbackGrid();
}

document.addEventListener('DOMContentLoaded', function() {
  initLandingDynamicBlocks();
  const savedLanguage = localStorage.getItem('selectedLanguage') || 'ar';
  void changeLanguage(savedLanguage);
});
window.initLandingDynamicBlocks = initLandingDynamicBlocks;

document.addEventListener('DOMContentLoaded', function() {
  const faqItems = document.querySelectorAll('.faq-item');

  faqItems.forEach(item => {
    const question = item.querySelector('.faq-question');
    question.addEventListener('click', () => {
      item.classList.toggle('active');
    });
  });
});

document.addEventListener('DOMContentLoaded', function() {
  const header = document.querySelector('.landing-header');
  const formSection = document.querySelector('#muh-eform') || document.querySelector('.muh-eform');

  if (!header || !formSection || typeof IntersectionObserver !== 'function') {
    return;
  }

  const syncHeaderVisibility = (entries) => {
    entries.forEach((entry) => {
      header.classList.toggle('is-hidden', entry.isIntersecting);
    });
  };

  const observer = new IntersectionObserver(syncHeaderVisibility, {
    root: null,
    threshold: 0.1
  });

  observer.observe(formSection);
});


document.addEventListener('DOMContentLoaded', function() {
  document.addEventListener('click', function(event) {
    const cta = event.target.closest('a[href="#muh-eform"]');
    if (!cta) {
      return;
    }

    event.preventDefault();
    let urls = {};
    try { urls = JSON.parse(document.getElementById('rox-checkout-urls').textContent); } catch (e) {}
    const lang = (typeof currentLang !== 'undefined' && currentLang) || localStorage.getItem('selectedLanguage') || 'ar';
    const url = urls[lang] || urls.ar || urls.en || urls.it;
    if (url) { window.location.href = url; }
  });
});
window.initFeedbackGrid = initFeedbackGrid;

document.addEventListener('DOMContentLoaded', function() {
  const lightbox = document.createElement('div');
  lightbox.className = 'about-lightbox';
  lightbox.setAttribute('aria-hidden', 'true');
  lightbox.innerHTML = '<button type="button" class="about-lightbox-close" aria-label="Close">&times;</button><img src="" alt="">';
  document.body.appendChild(lightbox);

  const lightboxImg = lightbox.querySelector('img');
  const closeBtn = lightbox.querySelector('.about-lightbox-close');

  const closeLightbox = () => {
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    lightboxImg.setAttribute('src', '');
  };

  document.addEventListener('click', function(event) {
    const trigger = event.target.closest('.about-achievement-gallery img');
    if (!trigger) {
      return;
    }

    const src = trigger.getAttribute('src');
    if (!src) {
      return;
    }

    lightboxImg.setAttribute('src', src);
    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
  });

  lightbox.addEventListener('click', function(event) {
    if (event.target === lightbox || event.target === closeBtn) {
      closeLightbox();
    }
  });

  document.addEventListener('keydown', function(event) {
    if ((event.key === 'Escape' || event.key === 'Esc') && lightbox.classList.contains('is-open')) {
      closeLightbox();
    }
  });
});

/**
 * Swap the hero poster for the real Vimeo embed on first interaction.
 *
 * The player pulls roughly a megabyte of third-party JS/CSS. Loading it only
 * when the visitor starts the video keeps it off the critical path, and costs
 * the same single click it always did.
 */
function initVideoFacade() {
  document.querySelectorAll('.muh-video-facade').forEach(function (facade) {
    const embedUrl = facade.getAttribute('data-embed-url');
    if (!embedUrl) {
      return;
    }

    const activate = function () {
      if (facade.dataset.activated === '1') {
        return;
      }
      facade.dataset.activated = '1';

      const separator = embedUrl.indexOf('?') === -1 ? '?' : '&';
      const iframe = document.createElement('iframe');
      iframe.src = embedUrl + separator + 'autoplay=1';
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share');
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      iframe.setAttribute('title', 'Landing video');
      iframe.setAttribute('allowfullscreen', '');
      iframe.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;';
      facade.replaceWith(iframe);
    };

    facade.addEventListener('click', activate);
    facade.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        activate();
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', initVideoFacade);

/**
 * Load deferred images (data-src) after the page has finished downloading.
 *
 * Loom serves an animated GIF poster, which is what gives a review card its
 * moving preview -- but they run to a couple of megabytes each, so they must
 * stay off the critical path and only start near the viewport.
 */
function initDeferredMedia() {
  const nodes = Array.from(document.querySelectorAll('img[data-src], [data-bg]'));
  if (nodes.length === 0) {
    return;
  }

  const reveal = (node) => {
    if (node.dataset.deferredLoaded === '1') {
      return;
    }
    node.dataset.deferredLoaded = '1';
    if (node.dataset.bg) {
      node.style.backgroundImage = "url('" + node.dataset.bg + "')";
    } else if (node.dataset.src) {
      node.src = node.dataset.src;
    }
  };

  const start = () => {
    if (typeof IntersectionObserver !== 'function') {
      nodes.forEach(reveal);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          reveal(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '300px' });
    nodes.forEach((node) => observer.observe(node));
  };

  if (document.readyState === 'complete') {
    start();
  } else {
    window.addEventListener('load', start, { once: true });
  }
}

document.addEventListener('DOMContentLoaded', initDeferredMedia);


function buildCheckoutUrl({ lang, fullName, iso2, dialCode, phone, urls }) {
  const url = (urls || {})[lang] || (urls || {}).ar || (urls || {}).it;
  const normalizedFullName = String(fullName || '').trim().replace(/\s+/g, ' ');
  const [firstName, ...lastName] = normalizedFullName.split(/\s+/);
  const phoneValue = String(phone || '').trim();
  const params = new URLSearchParams({
    'checkout[shipping_address][first_name]': firstName || '',
    'checkout[shipping_address][country]': String(iso2 || '').toUpperCase(),
    'attributes[phone]': phoneValue.startsWith('+') ? '+' + phoneValue.replace(/\D/g, '') : '+' + String(dialCode || '').replace(/\D/g, '') + phoneValue.replace(/\D/g, ''),
    'attributes[source]': 'roxecom-landing'
  });
  if (normalizedFullName) params.set('attributes[full_name]', normalizedFullName);
  if (lastName.length) params.set('checkout[shipping_address][last_name]', lastName.join(' '));
  return url + (url.includes('?') ? '&' : '?') + params;
}

window.RoxCheckout = { buildCheckoutUrl };

function formError(field, message) {
  const wrapper = field.closest('.form-field, .muh-form-group');
  if (!wrapper) return;
  wrapper.classList.add('has-error');
  const error = wrapper.querySelector('.field-error');
  if (error) { error.textContent = message || ''; error.hidden = false; error.classList.add('is-visible'); }
}

function clearFormError(field) {
  const wrapper = field.closest('.form-field, .muh-form-group');
  if (!wrapper) return;
  wrapper.classList.remove('has-error');
  wrapper.querySelectorAll('.field-error').forEach(error => { error.textContent = ''; error.hidden = true; error.classList.remove('is-visible'); });
}

function countryData(input) {
  try { return window.jQuery(input).countrySelect('getSelectedCountryData') || { iso2: 'it' }; } catch { return { iso2: 'it' }; }
}

function syncCountry(input) {
  const form = input.closest('form');
  const data = countryData(input);
  const iso2 = String(data.iso2 || 'it').toUpperCase();
  let dialCode = '39';
  try { dialCode = window.libphonenumber.getCountryCallingCode(iso2); } catch {}
  const dial = form.querySelector('.muh-dial-code');
  const hidden = form.querySelector('[name="phone_dial_code"]');
  if (dial) { dial.textContent = '+' + dialCode; dial.dataset.dialCode = dialCode; }
  if (hidden) hidden.value = dialCode;
  updatePhonePlaceholder(input);
}

function updatePhonePlaceholder(input) {
  const placeholder = { it: '312 345 6789', ma: '612-345678', fr: '06 12 34 56 78', es: '612 34 56 78', gb: '07400 123456' }[String(countryData(input).iso2 || '').toLowerCase()] || '201-555-0123';
  const phone = input.closest('form').querySelector('[name="phone"]');
  if (phone) phone.placeholder = placeholder;
}

function ensureCountrySearch(input) {
  const wrap = input.closest('.country-select');
  if (!wrap || wrap.dataset.searchInit) return;
  const list = wrap.querySelector('.country-list');
  const dropdown = wrap.querySelector('.flag-dropdown');
  if (!list || !dropdown) return;
  const search = document.createElement('input');
  search.type = 'text';
  search.className = 'muh-country-search';
  search.placeholder = 'Search';
  dropdown.prepend(search);
  const filter = () => {
    const term = search.value.toLowerCase().trim();
    list.querySelectorAll('li.country').forEach(item => { item.style.display = !term || item.textContent.toLowerCase().includes(term) ? '' : 'none'; });
    list.querySelectorAll('li.divider').forEach(item => { item.style.display = term ? 'none' : ''; });
  };
  const syncOpenState = () => {
    const isOpen = !list.classList.contains('hide');
    wrap.classList.toggle('is-open', isOpen);
    search.style.display = isOpen ? 'block' : 'none';
    if (!isOpen) { search.value = ''; filter(); }
  };
  search.addEventListener('input', filter);
  ['keydown', 'click'].forEach(type => search.addEventListener(type, event => event.stopPropagation()));
  wrap.querySelector('.selected-flag').addEventListener('click', () => setTimeout(() => { syncOpenState(); if (!list.classList.contains('hide')) search.focus(); }));
  document.addEventListener('click', event => { if (!wrap.contains(event.target)) setTimeout(syncOpenState); });
  document.addEventListener('touchstart', event => { if (!wrap.contains(event.target)) setTimeout(syncOpenState); });
  list.addEventListener('click', event => { if (event.target.closest('li.country')) setTimeout(syncOpenState); });
  syncOpenState();
  wrap.dataset.searchInit = '1';
}

function initCheckoutForm(form) {
  if (form.dataset.roxCheckoutInit) return;
  form.dataset.roxCheckoutInit = '1';
  const country = form.querySelector('.muh-phone-country');
  const phone = form.querySelector('[name="phone"]');
  if (country && window.jQuery?.fn.countrySelect) {
    window.jQuery(country).countrySelect({ preferredCountries: ['it', 'ma', 'fr', 'es', 'gb'], defaultCountry: 'it' });
    window.jQuery(country).countrySelect('selectCountry', 'it').on('change input', () => syncCountry(country));
    ensureCountrySearch(country);
  }
  if (country) syncCountry(country);
  if (phone) phone.addEventListener('input', () => { phone.value = phone.value.replace(/[^0-9\s\-().]/g, ''); });
  form.querySelectorAll('input, select, textarea').forEach(field => field.addEventListener('input', () => clearFormError(field)));
  form.addEventListener('submit', event => {
    event.preventDefault();
    let firstInvalid;
    const radioNames = new Set();
    form.querySelectorAll('[required]').forEach(field => {
      if (field.type === 'radio') {
        if (radioNames.has(field.name)) return;
        radioNames.add(field.name);
        if (form.querySelector('[name="' + field.name + '"]:checked')) return clearFormError(field);
        formError(field, field.dataset.requiredMessage);
        firstInvalid ||= field;
        return;
      }
      const value = field.type === 'radio' || field.type === 'checkbox' ? field.checked : field.value.trim();
      const invalid = !value || (field.type === 'tel' && field.value.replace(/\D/g, '').length < 6);
      if (invalid) { formError(field, field.dataset[!value ? 'requiredMessage' : 'invalidMessage']); firstInvalid ||= field; } else clearFormError(field);
    });
    if (firstInvalid) return firstInvalid.focus();
    const urls = JSON.parse(document.getElementById('rox-checkout-urls').textContent);
    const data = country ? countryData(country) : { iso2: 'it' };
    const dialCode = form.querySelector('[name="phone_dial_code"]')?.value || '39';
    location.href = buildCheckoutUrl({ lang: form.querySelector('[name="selected_lang"]')?.value, fullName: form.querySelector('[name="full_name"]')?.value, iso2: data.iso2, dialCode, phone: phone?.value, urls });
  });
}

document.addEventListener('DOMContentLoaded', () => document.querySelectorAll('.muh-eform').forEach(initCheckoutForm));

/* ROX native VSL: muted autoplay + tap for sound from start */
(function(){
  function initRoxVsl(){
    var v=document.getElementById('rox-vsl');
    var btn=document.getElementById('rox-vsl-unmute');
    if(!v) return;
    try{ v.muted=true; var pr=v.play(); if(pr&&pr.catch) pr.catch(function(){}); }catch(e){}
    function playWithSound(){
      v.loop=false; v.muted=false; v.controls=true;
      try{ v.currentTime=0; }catch(e){}
      var pr=v.play(); if(pr&&pr.catch) pr.catch(function(){});
      if(btn) btn.classList.add('is-hidden');
    }
    if(btn) btn.addEventListener('click', playWithSound);
    v.addEventListener('click', function(){ if(v.muted) playWithSound(); });
  }
  if(document.readyState!=='loading') initRoxVsl();
  else document.addEventListener('DOMContentLoaded', initRoxVsl);
})();
